import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import queryString from 'query-string';
import { Helmet } from 'react-helmet';
import { useHistory, useParams } from 'react-router';
import { toast } from 'react-toastify';

import {
  Card,
  Loader,
  Select,
} from '~/components';
import { useStateVar } from '~/helpers/useStateVar';
import { buildTabLink, IntegrInlineTabs } from '~/pages/Analysis/Integrations/IntegrLayout';
import { IntegrPerfilContents } from '~/pages/Analysis/Integrations/IntegrPerfil/index';
import { IntegrRealTimeContents } from '~/pages/Analysis/Integrations/IntegrRealTime/index';
import { TUnitInfo, UnitLayout } from '~/pages/Analysis/Units/UnitLayout';
import { apiCall, ApiResps } from '~/providers';
import { CoolAutomationHist } from '../../Integrations/IntegrHistory/CoolAutomationHist';
import { IntegrDevInfo } from '../../Integrations/IntegrPerfil/IntegrDevInfo';
import { CoolAutomationEdit } from '../../Integrations/IntegrEdit/CoolAutomationEdit';
import { withTransaction } from '@elastic/apm-rum-react';
import { generateNameFormatted } from '~/helpers/titleHelper';

export const UnitVRF = (): JSX.Element => {
  const history = useHistory();
  const { t } = useTranslation();
  const routeParams = useParams<{ unitId: string }>();
  const [state, render, setState] = useStateVar({
    unitId: Number(routeParams.unitId),
    isLoading: true,
    unitInfo: null as null| TUnitInfo,
    devicesList: [] as { dataSource: string, integrId: string }[],
    selectedDevice: null as null|{ dataSource: string, integrId: string },
    isLoadingDevice: false,
    devInfoResp: null as (null|ApiResps['/get-integration-info']),
  });

  async function handleGetUnitInfo() {
    try {
      setState({ isLoading: true });
      const [
        unitInfo,
        { list: devicesList },
      ] = await Promise.all([
        apiCall('/clients/get-unit-info', { unitId: state.unitId }),
        apiCall('/get-integrations-list', { supplier: 'coolautomation', unitIds: [state.unitId], INCLUDE_UNIT_STATUS: ['DEACTIVATED', 'FORCED_UNINSTALL', 'INSTALLATION', 'OPERATION', 'MODERNIZATION'] }),
      ]);
      state.unitInfo = unitInfo;
      state.devicesList = devicesList;
      if (state.devicesList.length === 1) {
        state.selectedDevice = state.devicesList[0];
      }
    } catch (err) {
      console.log(err);
      toast.error(t('houveErro'));
    }
    setState({ isLoading: false });
  }

  useEffect(() => {
    handleGetUnitInfo();
  }, []);

  const queryPars = queryString.parse(history.location.search);

  useEffect(() => {
    (async function () {
      try {
        if (!state.devicesList.length) return;
        const selectedDeviceAux = queryPars.aba === 'tempo-real' || !queryPars.aba ? state.devicesList[0] : state.selectedDevice;
        if (!selectedDeviceAux) return;
        setState({ isLoadingDevice: true });
        const response = await apiCall('/get-integration-info', { supplier: 'coolautomation', integrId: selectedDeviceAux.integrId });
        state.devInfoResp = response;
      } catch (err) {
        toast.error(t('houveErro'));
        console.log(err);
      }
      setState({ isLoadingDevice: false });
    }());
  }, [state.selectedDevice, queryPars.aba, state.devicesList.length]);

  return (
    <>
      <Helmet>
        <title>{generateNameFormatted(state.unitInfo?.UNIT_NAME, t('sistemasVRF'))}</title>
      </Helmet>
      <UnitLayout unitInfo={state.unitInfo} />
      {(state.isLoading) && <Loader />}
      {(!state.isLoading) && (
        <>
          <div style={{ paddingTop: '10px' }}>
            {(state.isLoadingDevice) && <Loader />}
            {(!state.isLoadingDevice) && (
              <div>
                <div style={{ paddingTop: '20px', paddingBottom: '20px' }}>
                  <IntegrInlineTabs integrType="coolautomation" />
                </div>
                <SelectDeviceVRF
                  devicesList={state.devicesList}
                  selectedDevice={state.selectedDevice}
                  setState={setState}
                  page={queryPars.aba?.toString() || ''}
                />
                {
                  (queryPars.aba === 'perfil' || queryPars.aba === 'editar')
                  && state.selectedDevice && state.devInfoResp && (
                    <div style={{ paddingBottom: '30px' }}>
                      {queryPars.aba === 'perfil' ? (
                        <IntegrPerfilContents
                          integrType="coolautomation"
                          integrId={state.selectedDevice.dataSource}
                          devInfoResp={state.devInfoResp}
                          editLink={buildTabLink('editar', history)}
                        />
                      ) : (
                        <div style={{ paddingTop: '20px' }}>
                          <Card>
                            <div style={{ fontWeight: 'bold', paddingBottom: '35px', fontSize: '1.25em' }}>
                              {t('informacoes')}
                            </div>
                            <IntegrDevInfo devInfo={state.devInfoResp.info} />
                            <br />
                            <br />
                            <CoolAutomationEdit schedInfo={state.devInfoResp.coolautomation} />
                          </Card>
                        </div>
                      )}
                    </div>
                  )
                }
                {
                  queryPars.aba === 'historico' && state.devicesList?.length && (
                    <CoolAutomationHist deviceId={state.devicesList[0].integrId} />
                  )
                }
                {
                  (queryPars.aba === 'tempo-real' || !queryPars.aba) && state.devicesList?.length && state.devInfoResp && (
                    <IntegrRealTimeContents
                      integrType="coolautomation"
                      integrId={state.devicesList[0].dataSource}
                      devInfoResp={state.devInfoResp}
                    />
                  )
                }
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
};

export const SelectDeviceVRF = ({
  devicesList,
  selectedDevice,
  setState,
  page,
}: {
  devicesList: { dataSource: string; integrId: string }[];
  selectedDevice: { dataSource: string; integrId: string } | null;
  page: string;
  setState: ({ selectedDevice }) => void;
}): JSX.Element => {
  const { t } = useTranslation();

  return (
    <>
      {
        ['perfil', 'editar'].some((x) => x === page) && (
          <div style={{ maxWidth: '400px', paddingTop: '20px' }}>
            <Select
              options={devicesList}
              value={selectedDevice}
              placeholder={t('dispositivo')}
              propLabel="dataSource"
              onSelect={(opt) => setState({ selectedDevice: opt })}
              notNull
            />
          </div>
        )
      }
    </>

  );
};

export default withTransaction('UnitVRF', 'component')(UnitVRF);
